import { Maybe } from '@crocodille/util';
import { imageProxyUrl, localized } from '@routing';
import Head from 'next/head';
import { useRouter } from '@framework/router';
import { createContext, useContext } from 'react';

export interface PageMetaContextValue {
  readonly absoluteUrl: string;
}

export const PageMetaContext = createContext<PageMetaContextValue>({
  absoluteUrl: '',
});

export interface PageMetaProps {
  readonly pageTitle: string;
  readonly description: string;
  readonly image?: Maybe<string>;
}

const PageMeta: React.FC<PageMetaProps> = ({
  pageTitle,
  description,
  image: imageRelativeSrc,
}) => {
  const { absoluteUrl } = useContext(PageMetaContext);
  const { locales, locale, asPath } = useRouter();

  const alternateUrls: {
    readonly hrefLang: string;
    readonly href: string;
  }[] = [];

  for (const alternativeLocale of locales ?? []) {
    if (alternativeLocale === locale) {
      continue;
    }

    alternateUrls.push({
      hrefLang: alternativeLocale,
      href: absoluteUrl + localized(alternativeLocale, asPath),
    });
  }

  const canonicalUrl = absoluteUrl + asPath;

  imageRelativeSrc ??= locale === 'cs' ? '/social-cs.jpg' : '/social-en.jpg';

  const image = imageRelativeSrc
    ? absoluteUrl + imageProxyUrl({ url: imageRelativeSrc, w: 1200 })
    : null;
  const title = `${pageTitle} | BB Domů`;

  return (
    <Head>
      {/* General */}
      <title key="page-title">{title}</title>
      <meta key="description" name="description" content={description} />
      {image ? <meta key="image" name="image" content={image} /> : null}
      <link key="canonical" rel="canonical" href={canonicalUrl} />
      {alternateUrls.map((link) => (
        <link key={'alternate-' + link.hrefLang} rel="alternate" {...link} />
      ))}
      {/* Twitter */}
      <meta
        key="twitter-card"
        name="twitter:card"
        content="summary_large_image"
      />
      {/* <meta key="twitter-site" name="twitter:site" content={TWITTER_HANDLE} /> */}
      <meta key="twitter-title" name="twitter:title" content={title} />
      <meta
        key="twitter-description"
        name="twitter:description"
        content={description}
      />
      {image ? (
        <meta key="twitter-image" name="twitter:image" content={image} />
      ) : null}
      {/* Open Graph */}
      <meta key="og-type" property="og:type" content="article" />
      <meta key="og-title" property="og:title" content={title} />
      <meta
        key="og-description"
        property="og:description"
        content={description}
      />
      <meta key="og-url" property="og:url" content={canonicalUrl} />
      {image ? (
        <meta key="og-image" property="og:image" content={image} />
      ) : null}
    </Head>
  );
};

export default PageMeta;
